import { Currency } from 'enums';
import { DateTime } from 'luxon';

export enum Gender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  NOT_CHOSEN = '',
}

export enum CalculationMode {
  CALCULATED = 'CALCULATED',
  MANUAL = 'MANUAL',
  REQUIRED = 'REQUIRED',
  NONE = 'NONE',
}

export enum TargetType {
  RETIREMENT = 'RETIREMENT',
  CARE = 'CARE',
  TRAVELLING = 'TRAVELLING',
  WEDDING = 'WEDDING',
  CAR = 'CAR',
  OTHER = 'OTHER',
}

export enum Frequency {
  SINGLE = 'SINGLE',
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
  REPEAT_EVERY = 'REPEAT_EVERY',
}

export enum Durability {
  PERIOD = 'PERIOD',
  LIFETIME = 'LIFETIME',
  UNTIL = 'UNTIL',
}

export interface Inflation {
  enabled: boolean;
  rate: number;
  valuation: boolean;
  type: string;
}

export enum Role {
  PARTNER = 'PARTNER',
  SUBSIDIARY = 'SUBSIDIARY',
  DEPUTY = 'DEPUTY',
  DIRECT = 'DIRECT',
}

export interface SingleValueResponse<T> {
  value: T;
}

interface RiskCategoryProfiles {
  BACKGROUND: number;
  EXPERIENCE: number;
  HORIZON: number;
  RISK_TOLERANCE: number;
  KNOWLEDGE: number;
}

export interface RiskProfiles {
  result: {
    categoryProfiles: RiskCategoryProfiles;
    profile: number;
    profileChange?: number;
    esg?: boolean;
    skipped: boolean;
    linked?: boolean;
  };
}

export interface QuestionnaireQuestions {
  [key: string]: string | string[];
}

export interface Value {
  value: number;
}

export interface AmountValue extends Value {
  currency: Currency;
}

export interface YearAndMonth {
  years: number;
  months: number;
}

export interface AssetCategory {
  share: number;
  code: string;
  title: string;
  assetClasses: BucketAssetClass[];
  current?: {
    share: number;
  }
}

export interface BucketAssetClass {
  share: number;
  code: string;
  title: string;
  assets?: ClassesAsset[];
}

export interface ClassesAsset {
  code: string;
  currency: Currency;
  isin: string;
  woodId: string;
  kid: string;
  link: string;
  report: string;
  share: number;
  title: string;
  originalTitle?: string;
  description: string;
  ter?: number;
  swaps?: {
    title: string;
    current: {
      profit: number;
      currency: Currency;
    };
  }[];
  current: {
    share: number;
    value: number;
    currency: Currency;
    profit: number;
  };
  assetCategory?: string;
  assetClass?: string;
  maxShare?: number;
  esgScore?: number;
  risk?: number;
  varianceClass: string;
  statement?: { // PDF
    pieces: AmountValue;
    price: AmountValue;
    originalValue: AmountValue;
    exchangeRate: AmountValue;
    convertedValue: AmountValue;
  };

}

export enum ChartPeriods {
  LAST_1W = 'LAST_1W',
  LAST_1M = 'LAST_1M',
  LAST_3M = 'LAST_3M',
  LAST_6M = 'LAST_6M',
  LAST_1Y = 'LAST_YEAR',
  LAST_3Y = 'LAST_3Y',
  LAST_5Y = 'LAST_5Y',
  THIS_YEAR = 'THIS_YEAR',
  ALL_TIME = 'ALL_TIME',
  CUSTOM = 'CUSTOM'
}

export const chartPeriods = [
  { value: ChartPeriods.LAST_1W, label: 'accountDetail_graph_week', shift: { days: 7 } },
  { value: ChartPeriods.LAST_1M, label: 'accountDetail_graph_1month', shift: { months: 1 } },
  { value: ChartPeriods.LAST_3M, label: 'accountDetail_graph_3months', shift: { months: 3 } },
  { value: ChartPeriods.LAST_6M, label: 'accountDetail_graph_6months', shift: { months: 6 } },
  { value: ChartPeriods.LAST_1Y, label: 'accountDetail_graph_year', shift: { years: 1 } },
  { value: ChartPeriods.LAST_3Y, label: 'accountDetail_graph_3years', shift: { years: 3 } },
  { value: ChartPeriods.LAST_5Y, label: 'accountDetail_graph_5years', shift: { years: 5 } },
  { value: ChartPeriods.THIS_YEAR, label: 'accountDetail_graph_thisYear', start: DateTime.local().startOf('year').toMillis() },
  { value: ChartPeriods.ALL_TIME, label: 'accountDetail_graph_allTime' },
];
